import { useState } from 'react';
import { useAuthStore, apiClient } from '../store/auth.store';
import toast from 'react-hot-toast';

export default function TestEvent() {
  const { user } = useAuthStore();
  const [username, setUsername] = useState('TestUser');
  const [amount, setAmount] = useState('10.00');
  const [message, setMessage] = useState('This is a test donation!');
  const [avatar, setAvatar] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [sending, setSending] = useState(false);
  const [lastEvent, setLastEvent] = useState<any>(null);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await apiClient.post(
        '/events',
        {
          username,
          amount: parseFloat(amount),
          message,
          avatar,
          currency,
        }
      );
      setLastEvent(data.event);
      toast.success('Test event created! Check your widget.');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create event');
    } finally {
      setSending(false);
    }
  };

  const presetAvatars = [
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Zoe',
    'https://api.dicebear.com/7.x/avataaars/svg?seed=Max',
  ];

  return (
    <div className="test-event-page">
      <h1>Test Event Creator</h1>
      <p className="page-subtitle">Create test donation events to demo your widget</p>

      <div className="test-grid">
        <div className="card">
          <h2>Create Test Event</h2>
          <form onSubmit={handleSend}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Donator username"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Amount</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="10.00"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label>Currency</label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="RUB">RUB (₽)</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Donation message..."
                rows={3}
                maxLength={500}
              />
            </div>

            <div className="form-group">
              <label>Avatar (optional)</label>
              <input
                type="text"
                value={avatar}
                onChange={(e) => setAvatar(e.target.value)}
                placeholder="https://example.com/avatar.png"
              />
              <div className="avatar-presets">
                {presetAvatars.map((url) => (
                  <img
                    key={url}
                    src={url}
                    alt="preset"
                    className={`avatar-preset ${avatar === url ? 'selected' : ''}`}
                    onClick={() => setAvatar(url)}
                  />
                ))}
              </div>
            </div>

            <button type="submit" className="btn-primary" disabled={sending}>
              {sending ? 'Sending...' : 'Send Test Event'}
            </button>
          </form>
        </div>

        <div className="card">
          <h2>Quick Tests</h2>
          <div className="quick-tests">
            <button
              onClick={() => {
                setUsername('Alice');
                setAmount('5.00');
                setMessage('Great stream! Keep it up! 💜');
              }}
              className="quick-btn"
            >
              💜 Small Donation ($5)
            </button>
            <button
              onClick={() => {
                setUsername('Bob');
                setAmount('50.00');
                setMessage('You deserve it! 🔥');
              }}
              className="quick-btn"
            >
              🔥 Medium Donation ($50)
            </button>
            <button
              onClick={() => {
                setUsername('VIP_User');
                setAmount('100.00');
                setMessage('Love your content! Subscribe to my channel! 🎉');
              }}
              className="quick-btn"
            >
              🎉 Big Donation ($100)
            </button>
            <button
              onClick={() => {
                setUsername('Anonymous');
                setAmount('0.01');
                setMessage('');
              }}
              className="quick-btn"
            >
              👤 Anonymous Tip ($0.01)
            </button>
          </div>

          <h3 style={{ marginTop: 30 }}>API Example</h3>
          <div className="code-block">
            <pre>{`curl -X POST ${window.location.origin}/api/events \\
  -H "Authorization: Bearer ${user?.apiToken}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "username": "TestUser",
    "amount": 10.00,
    "message": "Hello!",
    "currency": "USD"
  }'`}</pre>
          </div>
        </div>
      </div>

      {lastEvent && (
        <div className="card last-event-card">
          <h2>Last Created Event</h2>
          <div className="event-preview">
            <div className="event-avatar-placeholder">
              {lastEvent.username.charAt(0).toUpperCase()}
            </div>
            <div className="event-details">
              <div className="event-username">{lastEvent.username}</div>
              <div className="event-amount">
                ${lastEvent.amount.toFixed(2)} {lastEvent.currency}
              </div>
              {lastEvent.message && <div className="event-message">{lastEvent.message}</div>}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .test-event-page h1 {
          font-size: 28px;
          margin-bottom: 5px;
        }
        .page-subtitle {
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 30px;
        }
        .test-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        .card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
        }
        .card h2 {
          font-size: 18px;
          margin-bottom: 20px;
        }
        .card h3 {
          font-size: 16px;
          margin-bottom: 12px;
          color: var(--text-secondary);
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-group label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }
        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 10px 14px;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
          outline: none;
        }
        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          border-color: var(--primary);
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }
        .avatar-presets {
          display: flex;
          gap: 10px;
          margin-top: 10px;
        }
        .avatar-preset {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          cursor: pointer;
          border: 2px solid transparent;
          transition: all 0.2s;
        }
        .avatar-preset:hover {
          border-color: var(--text-secondary);
        }
        .avatar-preset.selected {
          border-color: var(--primary);
        }
        .btn-primary {
          width: 100%;
          padding: 12px;
          background: var(--primary);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 15px;
          font-weight: 600;
        }
        .btn-primary:hover:not(:disabled) {
          background: var(--primary-hover);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .quick-tests {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .quick-btn {
          padding: 12px 16px;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 14px;
          text-align: left;
          transition: all 0.2s;
        }
        .quick-btn:hover {
          background: var(--border);
          border-color: var(--primary);
        }
        .code-block {
          background: var(--bg-input);
          border-radius: 8px;
          padding: 16px;
          overflow-x: auto;
        }
        .code-block pre {
          font-size: 12px;
          font-family: monospace;
          color: var(--text-secondary);
          white-space: pre-wrap;
          word-break: break-all;
        }
        .last-event-card {
          margin-bottom: 30px;
        }
        .event-preview {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px;
          background: var(--bg-input);
          border-radius: 10px;
          border-left: 4px solid var(--primary);
        }
        .event-avatar-placeholder {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: var(--primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .event-username {
          font-weight: 700;
          font-size: 16px;
          color: var(--primary);
        }
        .event-amount {
          font-size: 20px;
          font-weight: 700;
          margin-top: 4px;
        }
        .event-message {
          font-size: 14px;
          color: var(--text-secondary);
          margin-top: 6px;
        }
        @media (max-width: 768px) {
          .test-grid {
            grid-template-columns: 1fr;
          }
          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
